import { createAsyncThunk } from '@reduxjs/toolkit';
import { ILessonHistory, TLessonHistoryTutorAmountData } from '.';
import APIDataProvider, { TApiResponse } from '../../../api/ApiDataProvider';
import { ApiPaths } from '../../../api/APIPaths';
import { TLessonTypes, TMeetingSectionPaymentType } from '../SectionSlice';

export type TLessonHistoryListGet = {
  limit: number;
  offset: number;
  sortKey?: string;
  sortValue?: number;
  startDate?: string;
  endDate?: string;
  courseId?: string;
  studentId?: string;
  tutorId?: string;
  referenceKey?: string;
  curriculum?: string;
  grade?: string;
  subject?: string;
  lessonType?: TLessonTypes;
  paymentType?: TMeetingSectionPaymentType;
  includeTutorAmountData?: boolean;
};

export type TGetLessonHistoryById = {
  id: string;
};

export type TLessonHistoryRaiseConcern = {
  lessonHistoryId: string;
  concern: string;
};

const apiDataProvider = new APIDataProvider();

export const lessonHistoryListGet = createAsyncThunk(
  '/lesson-history/list',
  async ({
    limit,
    offset,
    sortKey,
    sortValue,
    startDate,
    endDate,
    courseId,
    studentId,
    tutorId,
    referenceKey,
    curriculum,
    grade,
    subject,
    lessonType,
    paymentType,
    includeTutorAmountData,
  }: TLessonHistoryListGet) => {
    let path = `${ApiPaths.lessonHistory}?limit=${limit}&offset=${offset}`;

    if (sortKey) {
      path += `&sortKey=${sortKey}`;
    }

    if (sortValue) {
      path += `&sortValue=${sortValue}`;
    }

    if (startDate) {
      path += `&startDate=${startDate}`;
    }

    if (endDate) {
      path += `&endDate=${endDate}`;
    }

    if (courseId) {
      path += `&courseId=${courseId}`;
    }

    if (studentId) {
      path += `&studentId=${studentId}`;
    }

    if (tutorId) {
      path += `&tutorId=${tutorId}`;
    }

    if (referenceKey) {
      path += `&referenceKey=${referenceKey}`;
    }

    if (curriculum) {
      path += `&curriculum=${curriculum}`;
    }

    if (grade) {
      path += `&grade=${grade}`;
    }

    if (subject) {
      path += `&subject=${subject}`;
    }

    if (lessonType) {
      path += `&lessonType=${lessonType}`;
    }

    if (paymentType) {
      path += `&paymentType=${paymentType}`;
    }

    if (includeTutorAmountData) {
      path += `&includeTutorAmountData=1`;
    }

    const response = await apiDataProvider.get<
      TApiResponse<{
        offset: number;
        limit: number;
        totalDocs: number;
        docs: Array<ILessonHistory>;
        tutorAmountData?: TLessonHistoryTutorAmountData;
      }>
    >(path);

    return response.data;
  }
);

export const getLessonHistoryById = createAsyncThunk(
  '/lesson-history/getOne',
  async ({ id }: TGetLessonHistoryById) => {
    const path = `${ApiPaths.lessonHistory}/${id}`;

    const response = await apiDataProvider.get<TApiResponse<ILessonHistory>>(
      path
    );

    return response.data;
  }
);

export const lessonHistoryRaiseConcern = createAsyncThunk(
  '/lesson-history/tutor-payments/raise-concern',
  async (data: TLessonHistoryRaiseConcern) => {
    const path = `${ApiPaths.lessonHistory}/tutor-payments/raise-concern`;

    const response = await apiDataProvider.put<TApiResponse<ILessonHistory>>(
      path,
      data
    );

    return response.data;
  }
);
